<script>
import { mapState } from 'vuex';
import flatPickr from 'vue-flatpickr-component';
import { DateTime } from 'luxon';
import debounce from 'lodash/debounce';
import map from 'lodash/map';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import ReportModel from '@models/Report';
import HeaderComponent from '@components/HeaderComponent';
import utility from '@mixins/utility';

export default {
  page() {
    return {
      title: this.$t('titles.reports'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    flatPickr,
    HeaderComponent,
  },
  mixins: [utility],
  data() {
    return {
      sortBy: 'id',
      sortDesc: true,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'selected', sortable: false, label: this.$t('table_labels.select') },
        { key: 'id', sortable: true },
        {
          key: 'meeting_date',
          label: this.$t('table_labels.start_date'),
          sortable: true,
          formatter: (value) => DateTime.fromSQL(value).toFormat('dd.MM.yyyy'),
        },
        // { key: 'company_id', label: this.$t('table_labels.customer'), sortable: false },
        { key: 'vip_id', label: this.$t('table_labels.vip'), sortable: false },
        { key: 'meeting_point', label: this.$t('table_labels.city_and_location'), sortable: true },
        { key: 'total_service_time', label: this.$t('table_labels.total_service_time'), sortable: false },
        { key: 'completed', label: this.$t('table_labels.status'), sortable: true },
        // { key: 'expenses_uploaded_at', label: this.$t('table_labels.expenses_uploaded'), sortable: true, class: 'text-left' },
        { key: 'expenses_paid', label: this.$t('table_labels.expenses_paid'), sortable: false },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
      filters: {
        search: null,
        meetingDateStart: null,
        meetingDateEnd: null,
      },
      selectionOptionSelected: null,
      selectionOptions: [
        { text: this.$t('fields.select_option'), value: null },
        { text: this.$t('fields.export_select'), value: 0 },
        { text: this.$t('fields.sum_service_times_select'), value: 1 },
      ],
      selectedItems: [],
      selectAll: false,
      showMoreOptions: false,
      reports: [],
      totalServiceTimeSum: null,
      serviceTimeDataMap: {},
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
    ...mapState({
      dateConfig: (state) => state.common.dateConfig,
    }),
  },
  watch: {
    filters: {
      handler() {
        this.$refs.asyncTable.refresh();
      },
      immediate: false,
      deep: true,
    },
    reports(newReports) {
      if (newReports.length > 0) {
        const updatedMap = { ...this.serviceTimeDataMap };
        newReports.forEach((r) => {
          updatedMap[r.id] = {
            total_service_time: r.total_service_time,
            only_costs: r.only_costs,
          };
        });
        this.serviceTimeDataMap = updatedMap;
      }
    },
  },
  methods: {
    clearSelected() {
      this.selectedItems = [];
      this.selectAll = false;
      this.totalServiceTimeSum = null;
    },
    calculateTotalServiceTime() {
      let totalMinutes = 0;
      this.selectedItems.forEach((item) => {
        const data = this.serviceTimeDataMap[item.id];
        if (data && data.total_service_time && !data.only_costs) {
          const timeParts = data.total_service_time.split(':');
          const hours = parseInt(timeParts[0], 10) || 0;
          const minutes = parseInt(timeParts[1], 10) || 0;
          totalMinutes += hours * 60 + minutes;
        }
      });
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      this.totalServiceTimeSum = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    },
    itemsProvider(ctx) {
      const sortBy = ctx.sortBy === 'id' ? ctx.sortBy : ctx.sortBy + ',id';
      return ReportModel.orderBy(this.sortDir + sortBy)
        .include('vip', 'expenses', 'daily_allowances')
        .where('search', this.filters.search)
        .where('meeting_date_start', this.filters.meetingDateStart)
        .where('meeting_date_end', this.filters.meetingDateEnd)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          this.reports = response.data;
          return items || [];
        })
        .catch(() => []);
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.report') }))) {
        this.$wait.start('report processing');
        const report = new ReportModel({ id });
        await report.delete();
        this.$wait.end('report processing');
        this.$refs.asyncTable.refresh();
      }
    },
    exportPdf(url, id) {
      this.$wait.start(`downloading${id}-${url}`);
      this.$http
        .get(`reports/${url}/${id}`, { responseType: 'blob' })
        .then((response) => {
          this.$wait.end(`downloading${id}-${url}`);
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          const fileName = this.getFileNameFromContentDisposition(String(headers['content-disposition']));
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName !== '' ? fileName : `Report #${id} ${url === 'export_expenses_pdf' ? 'expenses' : ''}`;
          link.click();
        })
        .catch(() => this.$wait.end(`downloading${id}-${url}`));
    },
    exportAllowancesPdf(item) {
      if (!item.completed) {
        this.$toasted.error(this.$t('errors.report_status_not_completed'));
      } else {
        this.exportPdf('export-daily-allowances', item.id);
      }
    },
    exportMultiExpensesPdf() {
      this.$wait.start(`downloading-multi-expenses-pdf`);
      const ids = map(this.selectedItems, 'id');
      this.$http
        .get(`reports/export_multi_expenses_pdf`, { params: { ids: ids }, responseType: 'blob' })
        .then((response) => {
          this.$wait.end(`downloading-multi-expenses-pdf`);
          this.clearSelected();
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          const fileName = this.getFileNameFromContentDisposition(String(headers['content-disposition']));
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName !== '' ? fileName : `Selected reports expenses`;
          link.click();
        })
        .catch(() => this.$wait.end(`downloading-multi-expenses-pdf`));
    },
    updateFilter: debounce(function (value) {
      this.filters.search = value;
    }, 500),
    selectAllReports() {
      if (this.selectAll) {
        this.$wait.start('select-all-reports');
        const params = {};
        if (this.filters.search) params['filter[search]'] = this.filters.search;
        if (this.filters.meetingDateStart) params['filter[meeting_date_start]'] = this.filters.meetingDateStart;
        if (this.filters.meetingDateEnd) params['filter[meeting_date_end]'] = this.filters.meetingDateEnd;

        this.$http
          .get('reports/all-ids', { params })
          .then((response) => {
            const serviceTimeDataMap = {};
            this.selectedItems = response.data.data.map((item) => {
              serviceTimeDataMap[item.id] = {
                total_service_time: item.total_service_time,
                only_costs: item.only_costs,
              };
              return { id: item.id };
            });
            this.serviceTimeDataMap = serviceTimeDataMap;
          })
          .catch(() => {
            this.selectAll = false;
          })
          .finally(() => {
            this.$wait.end('select-all-reports');
          });
      } else {
        this.selectedItems = [];
      }
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent
          :title="$t('titles.reports')"
          :button-text="$t('titles.create_report')"
          @button-pressed="$router.push({ name: 'reports.create' })"
        ></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-4 flex-column-reverse flex-sm-row">
      <b-col sm="12" md="12" lg="4" class="order-1">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span
            >&nbsp;
            <small class="pt-1">
              {{ $t('search.by_vip_or_location') }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.search" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter"></b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.search" @click="filters.search = ''">{{
              $t('actions.clear')
            }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col sm="12" md="12" lg="8" class="order-2 d-none d-lg-flex justify-content-end align-items-end">
        <b-btn
          class="cursor-pointer border-gray"
          :variant="showMoreOptions ? 'secondary' : 'outline-secondary'"
          size="md"
          @click.stop="showMoreOptions = !showMoreOptions"
        >
          <FontAwesomeIcon :icon="['fas', 'sliders-h']" />
        </b-btn>
      </b-col>
    </b-row>
    <b-row class="mb-4 d-flex d-lg-none justify-content-end">
      <b-col>
        <b-btn
          class="fa-pull-right cursor-pointer border-gray"
          :variant="showMoreOptions ? 'secondary' : 'outline-secondary'"
          size="md"
          @click.stop="showMoreOptions = !showMoreOptions"
        >
          <FontAwesomeIcon :icon="['fas', 'sliders-h']" />
        </b-btn>
      </b-col>
    </b-row>
    <b-collapse v-model="showMoreOptions">
      <b-row class="mb-0 mb-md-4">
        <b-col sm="12" md="6" lg="4" class="order-1 mb-4 mb-md-0">
          <HeaderComponent>
            <template v-slot:header>
              <span>{{ $t('placeholders.date_interval_start') }}</span>
            </template>
          </HeaderComponent>
          <b-input-group>
            <flat-pickr
              v-model="filters.meetingDateStart"
              :config="dateConfig"
              class="form-control-sm"
              aria-placeholder="Interval Start"
              :placeholder="$t('placeholders.select_date')"
            ></flat-pickr>
            <b-input-group-append>
              <b-btn
                variant="secondary"
                size="sm"
                class="text-white border-gray"
                :disabled="filters.meetingDateStart == '' || filters.meetingDateStart == null"
                @click.stop="filters.meetingDateStart = ''"
                >{{ $t('actions.clear') }}</b-btn
              >
            </b-input-group-append>
          </b-input-group>
        </b-col>
        <b-col sm="12" md="6" lg="4" class="order-2 mb-4 mb-md-0">
          <HeaderComponent>
            <template v-slot:header>
              <span>{{ $t('placeholders.date_interval_end') }}</span>
            </template>
          </HeaderComponent>
          <b-input-group>
            <flat-pickr
              v-model="filters.meetingDateEnd"
              :config="dateConfig"
              class="form-control-sm"
              aria-placeholder="Interval End"
              :placeholder="$t('placeholders.select_date')"
            ></flat-pickr>
            <b-input-group-append>
              <b-btn
                variant="secondary"
                size="sm"
                class="text-white border-gray"
                :disabled="filters.meetingDateEnd == '' || filters.meetingDateEnd == null"
                @click.stop="filters.meetingDateEnd = ''"
                >{{ $t('actions.clear') }}</b-btn
              >
            </b-input-group-append>
          </b-input-group>
        </b-col>
        <b-col sm="12" md="4" lg="4" class="order-3 mb-4 mb-md-0">
          <HeaderComponent>
            <template v-slot:header>
              <span>{{ $t('titles.selection_options') }}</span
              >&nbsp;
            </template>
          </HeaderComponent>
          <b-input-group size="sm">
            <b-form-select v-model="selectionOptionSelected" :options="selectionOptions" @change="clearSelected"></b-form-select>
            <b-input-group-append>
              <b-btn
                variant="secondary"
                size="sm"
                class="text-white border-gray"
                :disabled="selectionOptionSelected === null"
                @click="selectionOptionSelected = null"
                >{{ $t('actions.clear') }}</b-btn
              >
            </b-input-group-append>
          </b-input-group>
        </b-col>
      </b-row>
      <b-row class="mb-4">
        <b-col v-if="selectionOptionSelected === 0" sm="12" lg="4" md="4">
          <b-button size="sm" class="w-100" :disabled="selectedItems.length === 0" variant="dark" @click="exportMultiExpensesPdf">{{
            $t('titles.export_multi_expenses')
          }}
          </b-button>
        </b-col>
        <b-col v-else-if="selectionOptionSelected === 1" sm="12" lg="4" md="4">
          <HeaderComponent>
            <template v-slot:header>
              <span>{{ $t('table_labels.total_service_time') }}</span>
            </template>
          </HeaderComponent>
          <b-input-group>
            <b-form-input
              v-model="totalServiceTimeSum"
              size="sm"
              type="text"
              readonly
              :placeholder="$t('placeholders.total_service_time')"
            ></b-form-input>
            <b-input-group-append>
              <b-button
                block
                size="sm"
                :disabled="selectedItems.length === 0"
                variant="secondary"
                class="text-white"
                @click="calculateTotalServiceTime"
              >
                {{ $t('actions.sum') }}
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>
      </b-row>
    </b-collapse>
    <div id="reports-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('report processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        :items="itemsProvider"
        stacked="md"
        striped
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
        <template #head(selected)="data">
          <span>{{ data.label }}</span>
          <span>
            <FontAwesomeIcon v-if="$wait.is('select-all-reports')" icon="spinner" spin />
            <b-form-checkbox v-else id="checkboxSelectAll" v-model="selectAll" @change="selectAllReports" />
          </span>
        </template>
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          <template v-if="key == 'expenses_paid'">
            {{ label }}
          </template>
          <template v-else>
            {{ label }}
          </template>
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>
        <template v-slot:cell(selected)="data">
          <b-form-checkbox
            :id="'checkbox' + data.index"
            v-model="selectedItems"
            :value="{ id: data.item.id }"
          >
          </b-form-checkbox>
        </template>
        <!-- <template v-slot:cell(expenses_uploaded_at)="row">
          <FontAwesomeIcon v-if="row.item.expenses_uploaded_at != null" class="text-success" :icon="['fas', 'check-circle']" />
          <FontAwesomeIcon v-else class="text-danger" :icon="['fas', 'times-circle']" />
        </template>           -->
        <template v-slot:cell(expenses_paid)="row">
          <FontAwesomeIcon
            v-if="row.item.expenses.length"
            :icon="['fas', row.item.expenses_paid ? 'check-circle' : 'times-circle']"
            :class="row.item.expenses_paid ? 'text-success' : 'text-danger'"
          />
          <span v-else> - </span>
        </template>
        <template v-slot:cell(total_service_time)="row">
          <span v-if="!row.item.only_costs"> {{ row.item.total_service_time }} </span>
          <span v-else> - </span>
        </template>
        <template v-slot:cell(actions)="row">
          <b-btn class="mr-2" size="sm" variant="outline-primary" :to="{ name: 'reports.edit', params: { id: row.item.id } }">
            <FontAwesomeIcon v-if="!row.item.locked && !row.item.completed" :icon="['fas', 'edit']" />
            <FontAwesomeIcon v-else :icon="['fas', 'eye']" />
          </b-btn>
          <b-btn
            class="mr-2"
            variant="outline-primary"
            size="sm"
            :disabled="$wait.is(`downloading${row.item.id}-export_pdf`)"
            @click="exportPdf('export_pdf', row.item.id)"
          >
            <FontAwesomeIcon :icon="['fas', 'file-export']" />
            <FontAwesomeIcon v-if="$wait.is(`downloading${row.item.id}-export_pdf`)" class="ml-1" icon="spinner" spin />
          </b-btn>
          <b-btn
            class="mr-2"
            variant="outline-success"
            size="sm"
            :disabled="$wait.is(`downloading${row.item.id}-export_expenses_pdf`)"
            :title="$t('actions.export_expenses')"
            @click="exportPdf('export_expenses_pdf', row.item.id)"
          >
            <span class="p-1">€</span>
            <FontAwesomeIcon v-if="$wait.is(`downloading${row.item.id}-export_expenses_pdf`)" class="ml-1" icon="spinner" spin />
          </b-btn>
          <b-btn
            v-if="row.item.daily_allowances.length > 0"
            class="mr-2"
            variant="outline-success"
            size="sm"
            :disabled="$wait.is(`downloading${row.item.id}-export_allowances_pdf`)"
            :title="$t('actions.export_allowances')"
            @click="exportAllowancesPdf(row.item)"
          >
            <FontAwesomeIcon :icon="['fas', 'utensils']" />
            <FontAwesomeIcon v-if="$wait.is(`downloading${row.item.id}-export_expenses_pdf`)" class="ml-1" icon="spinner" spin />
          </b-btn>
          <b-btn v-if="!row.item.locked" size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
            <FontAwesomeIcon :icon="['fas', 'times-circle']" />
          </b-btn>
        </template>
        <template v-slot:cell(completed)="row">
          <b-badge v-if="row.item.completed" variant="success">Completed</b-badge>
          <b-badge v-else variant="danger">Draft</b-badge>
        </template>
        <!-- <template slot="company_id" slot-scope="row">
          {{ row.item.company.name }}
        </template> -->
        <template v-slot:cell(vip_id)="row">
          {{ row.item.vip ? row.item.vip.name : row.item.vip_description }}
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>

    <b-row>
      <b-col sm="12">
        <div class="d-flex justify-content-start align-items-left mt-2">
          <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
          <div class="text-secondary d-flex align-items-center justify-content-center mb-3 px-4">
            {{  $t('infos.showing_results', {
              start: (currentPage - 1) * perPage + 1,
              end: Math.min(currentPage * perPage, totalRows),
              total: totalRows
            }) }}
          </div>
        </div>
      </b-col>
    </b-row>
  </Layout>
</template>
